import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import StripePaymentForm from '../../components/billing/StripePaymentForm';

const API = process.env.REACT_APP_API_URL;

let stripePromise = null;
const getStripePromise = () => {
    if (!stripePromise && process.env.REACT_APP_STRIPE_PK) {
        stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PK);
    }
    return stripePromise;
};

const formatMoney = (amount, currency) =>
    new Intl.NumberFormat(undefined, { style: 'currency', currency: (currency || 'usd').toUpperCase() }).format(amount / 100);

const CheckoutPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const plan = searchParams.get('plan') || 'monthly';

    const [prices, setPrices] = useState(null);
    const [sub, setSub] = useState(null);          // last successful create_subscription response
    const [pageError, setPageError] = useState(null);
    const [couponInput, setCouponInput] = useState(searchParams.get('code') || '');
    const [couponError, setCouponError] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [paying, setPaying] = useState(false);
    const reqSeqRef = useRef(0);

    const createSubscription = useCallback(async (code) => {
        const res = await fetch(`${API}/api/create_subscription.php`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ plan: 'monthly', ...(code ? { code } : {}) }),
        });
        let data = null;
        try { data = await res.json(); } catch (_) { /* non-JSON body */ }
        return { res, data };
    }, []);

    // Initial load: prices + subscription (with any code passed from pricing page).
    useEffect(() => {
        // No publishable key: the render shows the config-error card; skip the
        // server call so misconfigured deploys don't mint orphan subscriptions.
        // (React StrictMode double-mounts in dev re-run this effect; the server
        // prunes incomplete subs and resumes an existing card-less trial, so
        // double-creates stay harmless.)
        if (!process.env.REACT_APP_STRIPE_PK) return;

        let cancelled = false;
        const seq = ++reqSeqRef.current;
        (async () => {
            try {
                const priceRes = await fetch(`${API}/api/get_prices.php`, { credentials: 'include' });
                const priceData = await priceRes.json();
                if (!cancelled && priceData.status === 'success') setPrices(priceData);

                const initialCode = searchParams.get('code') || '';
                const { res, data } = await createSubscription(initialCode || undefined);
                if (cancelled || seq !== reqSeqRef.current) return;
                if (res.status === 409) { navigate('/settings/profile'); return; }
                if (!res.ok || !data || data.status !== 'success') {
                    // A bad prefilled coupon shouldn't block checkout — retry bare once.
                    if (initialCode) {
                        const retry = await createSubscription(undefined);
                        if (!cancelled && seq === reqSeqRef.current && retry.res.ok && retry.data?.status === 'success') {
                            setCouponError(data?.message || 'Coupon could not be applied.');
                            handleSubResponse(retry.data);
                            return;
                        }
                    }
                    setPageError(data?.message || 'Could not start checkout. Please try again.');
                    return;
                }
                handleSubResponse(data);
            } catch (err) {
                if (!cancelled) setPageError('Could not start checkout. Please try again.');
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubResponse = (data) => {
        if (data.type === 'none') { navigate('/upgrade/success'); return; }
        setSub(data);
    };

    const applyCoupon = async () => {
        const code = couponInput.trim();
        if (!code) return;
        setCouponLoading(true);
        setCouponError(null);
        const seq = ++reqSeqRef.current;
        try {
            const { res, data } = await createSubscription(code);
            if (seq !== reqSeqRef.current) return;
            if (res.status === 409) { navigate('/settings/profile'); return; }
            if (!res.ok || !data || data.status !== 'success') {
                setCouponError(data?.message || 'Coupon could not be applied.');
                return; // keep the existing subscription + secret
            }
            handleSubResponse(data);
        } catch (err) {
            setCouponError('Network error. Please try again.');
        } finally {
            setCouponLoading(false);
        }
    };

    const elementsOptions = useMemo(() => sub && ({
        clientSecret: sub.client_secret,
        appearance: {
            theme: 'night',
            variables: {
                colorPrimary: '#06b6d4',
                colorBackground: '#0b0e14',
                colorText: '#e5e5e5',
                borderRadius: '8px',
            },
        },
    }), [sub]);

    if (!process.env.REACT_APP_STRIPE_PK) {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto bg-[#0b0e14]/60 border border-white/10 rounded-xl p-6">
                    <h1 className="text-xl font-bold mb-2">Checkout unavailable</h1>
                    <p className="text-neutral-400 text-sm">Payments are not configured. Please contact support.</p>
                </div>
            </div>
        );
    }

    if (pageError) {
        return (
            <div className="p-8 md:p-12 text-white text-center">
                <div className="max-w-md mx-auto bg-[#0b0e14]/60 border border-red-900/40 rounded-xl p-6">
                    <h1 className="text-xl font-bold mb-2">Something went wrong</h1>
                    <p className="text-neutral-400 text-sm mb-4">{pageError}</p>
                    <button onClick={() => window.location.reload()} className="bg-cyan-500 hover:bg-cyan-400 text-black font-semibold py-2 px-6 rounded-lg">
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    const monthlyPrice = prices?.monthly;
    const annualPrice = prices?.annual;
    const isTrial = sub?.type === 'setup';
    const trialStart = isTrial && sub.trial?.first_charge_at ? new Date(sub.trial.first_charge_at) : null;

    return (
        <div className="p-6 md:p-12 text-white">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-2xl font-bold mb-8">Upgrade to Premium</h1>
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Order summary */}
                    <div className="space-y-4">
                        <div className="rounded-xl border border-cyan-500/60 bg-[#0b0e14]/60 p-5">
                            <div className="flex justify-between items-baseline">
                                <div>
                                    <h2 className="font-semibold">Premium Monthly</h2>
                                    <p className="text-xs text-neutral-400">Unlimited AI optimizations. Cancel anytime.</p>
                                </div>
                                <span className="text-lg font-bold">{monthlyPrice ? `${formatMoney(monthlyPrice.amount, monthlyPrice.currency)}/mo` : '…'}</span>
                            </div>
                        </div>
                        <div className="rounded-xl border border-white/10 bg-[#0b0e14]/30 p-5 opacity-60">
                            <div className="flex justify-between items-baseline">
                                <div>
                                    <h2 className="font-semibold">Premium Annual</h2>
                                    <p className="text-xs text-neutral-400">{annualPrice ? `${formatMoney(annualPrice.amount, annualPrice.currency)}/yr` : ''}</p>
                                </div>
                                <span className="text-[11px] uppercase tracking-wider bg-white/10 px-2 py-1 rounded">Coming soon</span>
                            </div>
                        </div>

                        {/* Coupon */}
                        {!isTrial && (
                            <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
                                <div className="flex gap-2">
                                    <input
                                        className="flex-1 bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm placeholder-neutral-600 focus:ring-2 focus:ring-cyan-500/60 focus:outline-none"
                                        placeholder="Coupon code"
                                        value={couponInput}
                                        onChange={(e) => setCouponInput(e.target.value)}
                                    />
                                    <button onClick={applyCoupon} disabled={couponLoading || !couponInput.trim() || !sub || paying}
                                        className="px-4 py-2 text-sm bg-white/10 hover:bg-white/20 rounded-lg disabled:opacity-50">
                                        {couponLoading ? 'Checking…' : 'Apply'}
                                    </button>
                                </div>
                                {couponError && <p className="text-red-400 text-xs mt-2">{couponError}</p>}
                                {sub?.discount && <p className="text-green-400 text-xs mt-2">{sub.discount} applied</p>}
                            </div>
                        )}

                        {/* Total — always from the server */}
                        <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
                            <div className="flex justify-between">
                                <span className="text-neutral-400">Due today</span>
                                <span className="text-xl font-bold">{sub ? formatMoney(sub.amount_due, sub.currency) : '…'}</span>
                            </div>
                            {isTrial && sub.trial && (
                                <p className="text-xs text-neutral-400 mt-2">
                                    then {formatMoney(sub.trial.amount, sub.trial.currency)}/mo starting {trialStart ? trialStart.toLocaleDateString() : 'after your trial'}
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Payment */}
                    <div className="rounded-xl border border-white/10 bg-[#0b0e14]/60 p-5">
                        {sub && elementsOptions ? (
                            <Elements key={sub.client_secret} stripe={getStripePromise()} options={elementsOptions}>
                                <StripePaymentForm
                                    payLabel={isTrial ? 'Start free trial' : `Pay ${formatMoney(sub.amount_due, sub.currency)}`}
                                    mode={isTrial ? 'setup' : 'payment'}
                                    onSubmittingChange={setPaying}
                                />
                            </Elements>
                        ) : (
                            <div className="flex items-center justify-center h-40">
                                <div className="w-8 h-8 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default CheckoutPage;
